/**
 * Formatea bytes a una representación legible.
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

/**
 * Calcula la duración aproximada de un audio leyendo metadata.
 * Devuelve null si el browser no puede leerla.
 */
export function getAudioDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    const url = URL.createObjectURL(file);

    const cleanup = () => {
      URL.revokeObjectURL(url);
      audio.remove();
    };

    audio.addEventListener('loadedmetadata', () => {
      const duration = isFinite(audio.duration) ? audio.duration : null;
      cleanup();
      resolve(duration);
    });

    audio.addEventListener('error', () => {
      cleanup();
      resolve(null);
    });

    audio.src = url;
  });
}

/**
 * Formatea segundos como mm:ss o hh:mm:ss.
 */
export function formatDuration(seconds: number): string {
  const s = Math.round(seconds);
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return hh > 0 ? `${hh}:${pad(mm)}:${pad(ss)}` : `${mm}:${pad(ss)}`;
}

/**
 * Validación ligera del archivo. Ya no aplicamos cap de 25MB porque
 * chunkeamos automáticamente con ffmpeg.wasm.
 */
export function validateAudioFile(file: File): { ok: true } | { ok: false; reason: string } {
  const allowedExt = ['m4a', 'mp3', 'wav', 'mp4', 'flac', 'aac', 'ogg', 'aiff'];
  const ext = file.name.toLowerCase().split('.').pop() ?? '';

  if (!allowedExt.includes(ext)) {
    return {
      ok: false,
      reason: `Formato no soportado: .${ext}. Usa ${allowedExt.map((e) => '.' + e).join(', ')}.`,
    };
  }

  // Cap blando para proteger memoria del navegador. Audios > 500MB son
  // probablemente video o algo que conviene procesar offline.
  const SOFT_MAX = 500 * 1024 * 1024;
  if (file.size > SOFT_MAX) {
    return {
      ok: false,
      reason: `El archivo pesa ${formatBytes(file.size)}. Comprime el audio antes (ffmpeg, Audacity) — el límite recomendado es ${formatBytes(SOFT_MAX)}.`,
    };
  }

  if (file.size === 0) {
    return { ok: false, reason: 'El archivo está vacío.' };
  }

  return { ok: true };
}

/**
 * Formatea un timestamp epoch ms como fecha y hora local en español.
 */
export function formatDate(epochMs: number): string {
  return new Date(epochMs).toLocaleString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
